import {Component, Input, OnChanges, SimpleChanges} from "@angular/core";
import {FormControl} from "@angular/forms";
import {ProductOptions} from "./redemption-options";
import {cdnURL} from "site";
import * as moment from "moment";

@Component({
	selector: "group-options",
	template: `
		<div [ngClass]="{
			'groupoptions_row': true,
			'groupoptions_invalidrow': selectionForm.invalid
		}">
			<div class="groupoptions_header">
				<div>
					<img *ngIf="options.logoURL" class="groupoptions_typeimage" [src]="options.logoURL"/>
					<span *ngIf="!options.logoURL" class="groupoptions_typename">{{options.name}}</span>
				</div>
				<div *ngIf="selectionForm.invalid" class="groupoptions_message statuscolor-orange">
					<span class="fa fa-lg fa-exclamation-circle"></span>
					Please select <strong>one</strong> of these options.
				</div>
				<div *ngIf="options.optional" class="groupoptions_message statuscolor-blue">
					<span class="fa fa-lg fa-info-circle"></span> Optional
				</div>
			</div>

			<div class="groupoptions_optionslist">
				<div class="groupoptions_option" *ngIf="isPortable()">
					<div>
						<img *ngIf="isPortable()" src="https://static.garmincdn.com/apps/fly/img/devices/no-portable.jpg" width="65" height="75"/>
					</div>
					<div class="groupoptions_statussection">
						<div class="groupoptions_status">
							<label (click)="toggleSelected('noPortable')">
								<a *ngIf="!isSelected('noPortable')" class="button button-secondary">NO PORTABLE</a>
								<a *ngIf="isSelected('noPortable')" class="button button-primary">NO PORTABLE</a>
							</label>
						</div>
					</div>
				</div>
				<div class="groupoptions_option" *ngFor="let mergedOptions of options.productOptions; let i = index">
					<div *ngIf="mergedOptions.product; let product">
						<product-series-display [product]="product" [label]="labelName(i)"></product-series-display>
					</div>
					<div *ngIf="mergedOptions.bundle; let bundle">
						<p class="groupoptions_bundlename">{{bundle.description}}</p>
						<p class="groupoptions_bundlecomments">{{bundle.customerComments}}</p>
					</div>
					<div *ngIf="mergedOptions.device; let device">
						<device-display [deviceID]="device.id"></device-display>
					</div>
					<div class="groupoptions_statussection">
						<div class="groupoptions_status">
							<div *ngIf="couponCode != null && expectedEndDate != null"
								 class="groupoptions_numissues">
								Coverage until {{expectedEndDate | momentDate}}
							</div>
							<div *ngIf="isIncluded" class="statuscolor-green">
								<i class="fa fa-lg fa-check-circle"></i> Included
							</div>
							<div *ngIf="!isIncluded">
								<input [hidden]="true"
									[id]="labelName(i)"
									type="radio"
									[name]="controlID"
									value="{{mergedOptions.identifier}}">
								<label (click)="toggleSelected(mergedOptions.identifier)">
									<a *ngIf="!isSelected(mergedOptions.identifier)" class="button button-secondary">SELECT</a>
									<a *ngIf="isSelected(mergedOptions.identifier)" class="button button-primary">SELECTED</a>
								</label>
							</div>
						</div>
						<p *ngIf="mergedOptions.product && mergedOptions.product.series.length > 1" class="groupoptions_multiseriesmessage">
							These databases are included in one package.<br>
							Only one may be installed at a time.
						</p>
					</div>
				</div>
			</div>
		</div>
	`,
})

export class GroupOptionsComponent implements OnChanges {
	@Input() selectionForm: FormControl;
	@Input() controlID: string;
	@Input() options: ProductOptions;
	@Input() couponCode: string

	readonly COVERAGE_MAP_PATH = cdnURL + "/img/avdb_type_regions/";

	ngOnChanges(changes: SimpleChanges) {
		if (changes.options) {
			switch (this.options.groupType) {
				case "bundle":
					this.options.productOptions = _.sortByOrder(this.options.productOptions,
						[po => po.bundle.bundleType, po => po.bundle.description],
						["asc", "asc"]);
					break;
				case "device":
					this.options.productOptions = _.sortBy(this.options.productOptions, po => po.device.name);
					break;
				case "avdbType":
					this.options.productOptions = _.sortBy(this.options.productOptions, po => po.product.series[0].region.name);
					for (let po of this.options.productOptions) {
						// If a product includes multiple series, sort those by name.
						if (po.product.series.length > 1) {
							po.product.series = _.sortBy(po.product.series, series => series.region.name);
						}
					}
					break;
				default:
					// no-op
			}
		}
	}

	get numIssuesDisplay(): string {
		const headOption = this.options.productOptions[0];
		if (headOption.isAvdbProduct) {
			const numIssues = headOption.product.numberOfIssues;
			return `${numIssues} ${(numIssues === 1) ? "Issue" : "Issues"}`;
		}
		else {
			return "";
		}
	}

	get expectedEndDate(): moment.Moment {
		const headOption = this.options.productOptions[0];
		if (headOption.isAvdbProduct) {
            const endDates = [].concat.apply([], headOption.product.series.map(s =>
                s.coverageDurationOptions.map(o => o.expectedEndDate)
            ));

            let furthestEndDate = null;

            for (const date of endDates) {
                if (date == null) {
                    return null
                }

                if (furthestEndDate == null || date > furthestEndDate) {
                    furthestEndDate = date;
                }
            }

            return furthestEndDate
		}
		else {
			return null;
		}
	}

	labelName(index: number): string {
		return `${this.controlID}_${index}`;
	}

	get isIncluded(): boolean {
		return this.options.productOptions.length === 1 && !this.isPortable();
	}

	isSelected(value: string): boolean {
		return this.selectionForm.value === value;
	}

	toggleSelected(value: string): void {
		// For unknown reason, the form control does not work in IE11.
		// This hack of setting the value by click event fixes the issue for now until it is better understood.
		this.selectionForm.patchValue(value);
	}

	isPortable(): boolean{
		return this.options.groupType === 'device' && this.options.name.includes('portable');
	}
}
